import { FieldArray, Formik, getIn, useFormikContext } from 'formik';
import { isEmpty, uniqueId } from 'lodash';
import React, { useCallback } from 'react';
import * as Yup from 'yup';
import { X } from 'lucide-react';

import Form, { FormElement, Label } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import Button from '@dashboard/components/Button';
import { Button as ShadcnButton } from '@dashboard/components/ui/button';
import { Field } from '@dashboard/components/Formik/Form';
import { renderFields } from '@dashboard/components/Formik/formUtils';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { formatPriceForInput, formatPriceInput } from '@dashboard/services/formatting/price';
import { confirm } from '@dashboard/lib/confirm';

function createRule(config) {
  return {
    minQuantity: '',
    maxQuantity: '',
    discountType: 'amountOff',
    amountOff: '',
    percentOff: '',
    quantityCondition: config.defaultQuantityCondition,
    [config.itemsFieldName]: [],
    key: uniqueId(),
  };
}

function mapDiscountToFormValue(discount, currency, config) {
  return {
    ...discount,
    _isSaved: true,
    key: uniqueId(),
    quantityCondition: discount.quantityCondition || config.defaultQuantityCondition,
    [config.itemsFieldName]: discount[config.itemsFieldName] || [],
    discountType: discount.amountOff ? 'amountOff' : 'percentOff',
    amountOff: discount.amountOff
      ? formatPriceForInput({
          amount: discount.amountOff,
          currency,
          style: 'decimal',
        })
      : '',
    percentOff: discount.percentOff || '',
  };
}

function getDiscountRuleFields(fieldValues, config) {
  return [
    {
      name: 'quantityCondition',
      label: config.quantityConditionLabel,
      tooltip: config.quantityConditionTooltip,
      type: 'radioCard',
      options: [
        { label: config.allItemsLabel, value: config.allItemsValue },
        { label: config.perItemLabel, value: config.perItemValue },
      ],
      required: true,
    },
    {
      name: config.itemsFieldName,
      label: config.itemsLabel,
      type: 'listbox',
      multiple: true,
      visible: fieldValues.quantityCondition === config.perItemValue,
      placeholder: config.itemsPlaceholder,
      options: config.itemOptions,
    },
    {
      type: 'columns',
      columns: 2,
      gap: 'sm',
      fields: [
        {
          name: 'minQuantity',
          label: 'Min. quantity',
          placeholder: 'Min. quantity',
          type: 'number',
          required: true,
        },
        {
          name: 'maxQuantity',
          label: 'Max. quantity',
          placeholder: 'Max. quantity',
          type: 'number',
          optional: true,
        },
      ],
    },
    {
      type: 'columns',
      columns: 2,
      gap: 'sm',
      fields: [
        {
          name: 'discountType',
          label: 'Discount type',
          type: 'select',
          options: [
            { label: 'Fixed amount', value: 'amountOff' },
            { label: 'Percentage', value: 'percentOff' },
          ],
          required: true,
        },
        {
          name: 'amountOff',
          label: 'Discount amount',
          placeholder: 'Discount amount',
          type: 'string',
          visible: fieldValues.discountType === 'amountOff',
        },
        {
          name: 'percentOff',
          label: 'Discount %',
          placeholder: 'Discount %',
          type: 'number',
          visible: fieldValues.discountType === 'percentOff',
        },
      ],
    },
  ];
}

function DiscountRuleBlock({ index, name, values, setFieldValue, config, onDelete }) {
  const { errors, submitCount } = useFormikContext();
  const ruleValues = values[name][index];

  const fields = getDiscountRuleFields(ruleValues, config);

  const itemsFieldPath = `${name}.${index}.${config.itemsFieldName}`;
  const itemsError = getIn(errors, itemsFieldPath);
  const showItemsError = submitCount > 0 && itemsError && typeof itemsError === 'string';

  return (
    <div className="mb-6">
      <div className="mb-1 flex items-center justify-between gap-2">
        <Label isInline tooltip={config.quantityConditionTooltip}>
          {config.quantityConditionLabel}
        </Label>

        <ShadcnButton
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onDelete}
          className="-mr-1.5 shrink-0"
          aria-label="Delete discount rule"
        >
          <X className="size-4" />
        </ShadcnButton>
      </div>

      <div>
        {renderFields({
          fields,
          values,
          setFieldValue,
        }).map(field => (
          <Field
            key={field.name}
            values={values}
            setFieldValue={setFieldValue}
            field={{
              ...field,
              id: `${name}.${index}.${field.name}`,
              name: `${name}.${index}.${field.name}`,
              visible: field.visible === undefined ? true : field.visible,
              hideLabel: field.name === 'quantityCondition' || undefined,
              reduceMargin: true,
              ...(field.fields?.length > 0
                ? {
                    fields: field.fields.map(subField => ({
                      ...subField,
                      id: `${name}.${index}.${subField.name}`,
                      name: `${name}.${index}.${subField.name}`,
                    })),
                  }
                : {}),
            }}
          />
        ))}

        {showItemsError && <div className="my-1 text-red-600 dark:text-red-400">{itemsError}</div>}
      </div>
    </div>
  );
}

const BulkDiscountsForm = ({ config, savedDiscounts, currency, onSubmit }) => {
  const validationSchema = Yup.object().shape({
    discounts: Yup.array().of(
      Yup.object().shape({
        quantityCondition: Yup.string().required('Quantity condition is required'),
        [config.itemsFieldName]: Yup.array().when('quantityCondition', {
          is: config.perItemValue,
          then: Yup.array().min(1, config.itemsValidationMessage),
          otherwise: Yup.array(),
        }),
        minQuantity: Yup.number().required('Minimum quantity is required'),
        maxQuantity: Yup.number().nullable(),
        discountType: Yup.string().required('Discount type is required'),
        amountOff: Yup.string().when('discountType', {
          is: 'amountOff',
          then: Yup.string().required('Discount amount is required'),
          otherwise: Yup.string().nullable(),
        }),
        percentOff: Yup.number().when('discountType', {
          is: 'percentOff',
          then: Yup.number().required('Discount percentage is required'),
          otherwise: Yup.number().nullable(),
        }),
      }),
    ),
  });

  const handleDelete = useCallback(
    async ({ index, arrayHelpers, values, resetForm }) => {
      const rule = values.discounts[index];
      const isSavedRule = !!rule._isSaved;

      if (!isSavedRule) {
        arrayHelpers.remove(index);
        return;
      }

      try {
        await confirm('Delete this discount rule?', {
          confirm: 'Delete',
        });
      } catch {
        return;
      }

      const updatedDiscounts = values.discounts.filter((_, i) => i !== index);

      const transformedDiscounts = updatedDiscounts
        .filter(d => !!d._isSaved)
        .map(discount => ({
          ...discount,
          [config.itemsFieldName]:
            discount.quantityCondition === config.perItemValue
              ? discount[config.itemsFieldName]
              : [],
          amountOff:
            discount.discountType === 'amountOff'
              ? formatPriceInput(discount.amountOff, currency)
              : null,
          percentOff: discount.discountType === 'percentOff' ? discount.percentOff : null,
        }));

      try {
        const result = await onSubmit(transformedDiscounts, { isDelete: true });

        if (result?.data) {
          resetForm({
            values: {
              discounts: result.data.map(d => mapDiscountToFormValue(d, currency, config)),
            },
          });
        } else {
          arrayHelpers.remove(index);
        }
      } catch {
        arrayHelpers.remove(index);
      }
    },
    [config, currency, onSubmit],
  );

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        discounts: savedDiscounts.map(d => mapDiscountToFormValue(d, currency, config)),
      }}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const transformedDiscounts = values.discounts.map(discount => ({
          minQuantity: discount.minQuantity,
          maxQuantity: discount.maxQuantity,
          percentOff: discount.discountType === 'percentOff' ? discount.percentOff : null,
          amountOff:
            discount.discountType === 'amountOff'
              ? formatPriceInput(discount.amountOff, currency)
              : null,
          quantityCondition: discount.quantityCondition,
          [config.itemsFieldName]:
            discount.quantityCondition === config.perItemValue
              ? discount[config.itemsFieldName]
              : [],
        }));

        try {
          const result = await onSubmit(transformedDiscounts, { isDelete: false });

          if (result?.data) {
            actions.setStatus('');
            actions.resetForm({
              values: {
                discounts: result.data.map(d => mapDiscountToFormValue(d, currency, config)),
              },
            });
          } else if (result?.error) {
            const { status, errors } = transformApiErrorToFormik(result.error);

            if (!isEmpty(errors)) {
              actions.setErrors(errors);
            } else {
              actions.setStatus(status);
            }
          }
        } catch (error) {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, status, dirty, handleSubmit, setFieldValue, resetForm, errors }) => {
        const hasRules = values.discounts.length > 0;
        const isEmptyState = !hasRules;

        const showCancelAndDirtyText = dirty;

        return (
          <Form onSubmit={handleSubmit}>
            <FieldArray
              name="discounts"
              render={arrayHelpers => (
                <>
                  {values.discounts.map((discount, index) => (
                    <DiscountRuleBlock
                      key={discount.key || index}
                      index={index}
                      name="discounts"
                      values={values}
                      setFieldValue={setFieldValue}
                      config={config}
                      onDelete={() => handleDelete({ index, arrayHelpers, values, resetForm })}
                    />
                  ))}

                  {hasRules && (
                    <Button
                      handleClick={() => arrayHelpers.push(createRule(config))}
                      appearance="light"
                      size="small"
                      type="button"
                    >
                      + Add another
                    </Button>
                  )}
                </>
              )}
            />

            {isEmptyState && (
              <Button
                handleClick={() => {
                  setFieldValue('discounts', [createRule(config)]);
                }}
                appearance="primary"
                type="button"
              >
                + Add discount rule
              </Button>
            )}

            <FormElement>{status && status}</FormElement>

            {hasRules && (
              <FormSubmit align="end">
                {showCancelAndDirtyText && <FormSubmit.State visible />}

                {showCancelAndDirtyText && (
                  <FormSubmit.Cancel
                    onClick={() => {
                      resetForm();
                    }}
                  >
                    Cancel
                  </FormSubmit.Cancel>
                )}

                <FormSubmit.Button>Save</FormSubmit.Button>
              </FormSubmit>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default BulkDiscountsForm;
